/**
 * TerraMatrix Knowledge Service API Client
 *
 * Connects to the knowledge-service (port 8005) for articles,
 * compilation, lint, feedback, and knowledge management operations.
 */

import axios, {
  AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { config as appConfig } from '@/config';
import { handle401WithSilentRefresh } from './authRefresh';
import { getCurrentAuthTokens } from './auth-session';
import {
  applyValidatedExplicitOrganizationHeaders,
  applyValidatedStoredOrganizationHeaders,
  hasExplicitOrganizationHeaders,
} from '@/services/api/client';
import type {
  KnowledgeArticle,
  DomainKnowledgeEntry,
  KnowledgeLintReport,
  CompilationStatus,
  GraphData,
  PaginatedResult,
  TrendData,
  ConsensusData,
  KnowledgeGap,
  KnowledgeArtifact,
  CompletenessResult,
} from '@/types/knowledge';

const KNOWLEDGE_SERVICE_BASE_URL = appConfig.api.knowledgeServiceUrl;

interface KnowledgeRequestOptions {
  skipAuthRecovery?: boolean;
  suppressErrorLog?: boolean;
}

export const createKnowledgeServiceClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: KNOWLEDGE_SERVICE_BASE_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const tokens = getCurrentAuthTokens();

      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }

      const hasPerRequestOrgOverride =
        hasExplicitOrganizationHeaders(config.headers);

      if (hasPerRequestOrgOverride) {
        await applyValidatedExplicitOrganizationHeaders(config.headers);
      } else {
        await applyValidatedStoredOrganizationHeaders(config.headers);
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // 401: delegate to the shared silent-refresh-then-modal handler so
      // knowledge-service 401s recover identically to the other services.
      if (
        originalRequest &&
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.skipAuthRecovery
      ) {
        const hadToken = getCurrentAuthTokens();
        if (hadToken?.accessToken) {
          originalRequest._retry = true;
          try {
            return await handle401WithSilentRefresh(error, client);
          } catch {
            // Fall through to the normalized ApiError below.
          }
        }
      }

      const apiError = {
        code: error.response?.data?.code || 'UNKNOWN_ERROR',
        message: error.response?.data?.message || error.message || 'An unexpected error occurred',
        details: error.response?.data?.details,
        status: error.response?.status,
      };
      if (!error.config?.suppressErrorLog) {
        console.error('[knowledgeApi] Request failed:', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          apiError,
        });
      }
      return Promise.reject(apiError);
    },
  );

  return client;
};

const knowledgeClient = createKnowledgeServiceClient();

export type CompileAllResult = Record<string, unknown>;

// ===== Articles =====

export const getArticles = async (params?: {
  page?: number;
  limit?: number;
  domain_tags?: string[];
  status?: string;
  sort?: string;
  order?: string;
}, requestConfig?: KnowledgeRequestOptions): Promise<PaginatedResult<KnowledgeArticle>> => {
  const { data } = await knowledgeClient.get('/articles', {
    ...requestConfig,
    params,
  });
  return data;
};

export const getArticle = async (id: string): Promise<KnowledgeArticle> => {
  const { data } = await knowledgeClient.get(`/articles/${id}`);
  return data.data;
};

export const getArticleBySlug = async (slug: string): Promise<KnowledgeArticle> => {
  const { data } = await knowledgeClient.get(`/articles/slug/${slug}`);
  return data.data;
};

export const getArticleGraph = async (domainTags?: string[]): Promise<GraphData> => {
  const params = domainTags?.length ? { domain_tags: domainTags.join(',') } : {};
  const { data } = await knowledgeClient.get('/articles/graph', { params });
  return data.data;
};

export const getArticleSources = async (id: string): Promise<DomainKnowledgeEntry[]> => {
  const { data } = await knowledgeClient.get(`/articles/${id}/sources`);
  return data.data;
};

export const searchArticles = async (query: string, options?: { semantic?: boolean; limit?: number }): Promise<KnowledgeArticle[]> => {
  const { data } = await knowledgeClient.get('/articles/search', {
    params: { q: query, ...options },
  });
  return data.data;
};

export const recordArticleView = async (id: string): Promise<void> => {
  await knowledgeClient.post(`/articles/${id}/view`);
};

export const rateArticle = async (id: string, score: number): Promise<void> => {
  await knowledgeClient.post(`/articles/${id}/rate`, { score });
};

export const updateArticle = async (id: string, input: Partial<KnowledgeArticle>): Promise<KnowledgeArticle> => {
  const { data } = await knowledgeClient.put(`/articles/${id}`, input);
  return data.data;
};

export const deleteArticle = async (id: string): Promise<void> => {
  await knowledgeClient.delete(`/articles/${id}`);
};

// ===== Knowledge Entries =====

export const deleteKnowledgeEntry = async (id: string): Promise<void> => {
  await knowledgeClient.delete(`/knowledge/${id}`);
};

export const getKnowledgeEntries = async (params?: {
  page?: number;
  limit?: number;
  domain_tags?: string;
  ingestion_path?: string;
  trust_status?: string;
  category?: string;
}): Promise<PaginatedResult<DomainKnowledgeEntry>> => {
  const { data } = await knowledgeClient.get('/knowledge', { params });
  return data;
};

export const createKnowledgeEntry = async (input: {
  text: string;
  ingestion_path: string;
  domain_tags?: string[];
  visibility?: string;
  category?: string;
}): Promise<DomainKnowledgeEntry> => {
  const { data } = await knowledgeClient.post('/knowledge', input);
  return data.data;
};

export const searchKnowledge = async (query: string, options?: {
  domain_tags?: string[];
  trust_status?: string;
  limit?: number;
}): Promise<DomainKnowledgeEntry[]> => {
  const { data } = await knowledgeClient.get('/search', {
    params: { q: query, ...options },
  });
  return data.data || data.results || [];
};

// ===== Compilation =====

export const getCompilationStatus = async (): Promise<CompilationStatus> => {
  const { data } = await knowledgeClient.get('/compile/status');
  return data.data;
};

export const triggerCompilation = async (domainTag: string): Promise<{ articles: KnowledgeArticle[] }> => {
  const { data } = await knowledgeClient.post('/compile/domain', { domain_tag: domainTag });
  return data.data;
};

export const triggerCompileAll = async (): Promise<CompileAllResult[]> => {
  const { data } = await knowledgeClient.post('/compile/all');
  return data.data;
};

export const getPendingEntries = async (params?: { page?: number; limit?: number }): Promise<PaginatedResult<DomainKnowledgeEntry>> => {
  const { data } = await knowledgeClient.get('/compile/pending', { params });
  return data;
};

export const recompileArticle = async (id: string): Promise<{ articles: KnowledgeArticle[] }> => {
  const { data } = await knowledgeClient.post(`/compile/recompile/${id}`);
  return data.data;
};

// ===== Lint =====

export const getLatestLintReport = async (): Promise<KnowledgeLintReport | null> => {
  const { data } = await knowledgeClient.get('/lint/latest');
  return data.data;
};

export const getLintReports = async (params?: { page?: number; limit?: number }): Promise<PaginatedResult<KnowledgeLintReport>> => {
  const { data } = await knowledgeClient.get('/lint/reports', { params });
  return data;
};

export const triggerLint = async (domainTags?: string[]): Promise<KnowledgeLintReport> => {
  const { data } = await knowledgeClient.post('/lint/run', { domain_tags: domainTags });
  return data.data;
};

export const resolveLintFinding = async (reportId: string, findingId: string): Promise<void> => {
  await knowledgeClient.post(`/lint/reports/${reportId}/resolve/${findingId}`);
};

export const autoFixLintFinding = async (reportId: string, findingId: string): Promise<{ fixed: boolean }> => {
  const { data } = await knowledgeClient.post(`/lint/reports/${reportId}/auto-fix/${findingId}`);
  return data.data;
};

// ===== Community / Trust =====

export const confirmKnowledge = async (id: string): Promise<void> => {
  await knowledgeClient.post(`/community/${id}/confirm`);
};

export const disputeKnowledge = async (id: string, note?: string): Promise<void> => {
  await knowledgeClient.post(`/community/${id}/dispute`, { note });
};

export const saveKnowledge = async (id: string): Promise<void> => {
  await knowledgeClient.post(`/community/${id}/save`);
};

// ===== Trends =====

export const getTrendTimeline = async (knowledgeId: string): Promise<TrendData> => {
  const { data } = await knowledgeClient.get(`/trends/timeline/${knowledgeId}`);
  return data.data;
};

export const getConsensusDistribution = async (domainTag: string): Promise<ConsensusData> => {
  const { data } = await knowledgeClient.get(`/trends/consensus/${domainTag}`);
  return data.data;
};

// ===== Feedback =====

export const getRecentFeedback = async (params?: { page?: number; limit?: number }): Promise<PaginatedResult<DomainKnowledgeEntry>> => {
  const { data } = await knowledgeClient.get('/feedback/recent', { params });
  return data;
};

export const approveFeedback = async (id: string): Promise<void> => {
  await knowledgeClient.post(`/feedback/approve/${id}`);
};

export const rejectFeedback = async (id: string): Promise<void> => {
  await knowledgeClient.post(`/feedback/reject/${id}`);
};

// ===== Gaps =====

export const createGapsBatch = async (gaps: Array<{
  topic: string;
  description: string;
  category: string;
  gap_type: string;
  priority: string;
  domain_tags?: string[];
  metadata: {
    discovered_by: string;
    suggested_question: string;
  };
}>): Promise<KnowledgeGap[]> => {
  const { data } = await knowledgeClient.post('/gaps/batch', { gaps });
  return data.data;
};

export const getOpenGaps = async (params?: {
  domain_tags?: string[];
}): Promise<KnowledgeGap[]> => {
  const { data } = await knowledgeClient.get('/gaps/open', { params });
  return data.data;
};

export const getAllGaps = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  category?: string;
}): Promise<PaginatedResult<KnowledgeGap>> => {
  const { data } = await knowledgeClient.get('/gaps', { params });
  return data;
};

export const fillGap = async (gapId: string, knowledgeEntryId: string): Promise<void> => {
  await knowledgeClient.put(`/gaps/${gapId}/fill`, { knowledge_entry_id: knowledgeEntryId });
};

export const enrichGap = async (gapId: string, knowledgeEntryId: string): Promise<void> => {
  await knowledgeClient.put(`/gaps/${gapId}/enrich`, { knowledge_entry_id: knowledgeEntryId });
};

export const deferGap = async (gapId: string): Promise<void> => {
  await knowledgeClient.put(`/gaps/${gapId}/defer`);
};

export const markGapSufficient = async (gapId: string): Promise<void> => {
  await knowledgeClient.put(`/gaps/${gapId}/sufficient`);
};

export const deleteGap = async (gapId: string): Promise<void> => {
  await knowledgeClient.delete(`/gaps/${gapId}`);
};

// ===== Completeness =====

export const getCompleteness = async (): Promise<CompletenessResult> => {
  const { data } = await knowledgeClient.get('/completeness');
  return data.data;
};

export const triggerCompletenessAssessment = async (domainTags?: string[]): Promise<CompletenessResult> => {
  const { data } = await knowledgeClient.post('/completeness/assess', { domain_tags: domainTags });
  return data.data;
};

export const discoverNewGaps = async (domainTags?: string[]): Promise<KnowledgeGap[]> => {
  const { data } = await knowledgeClient.post('/completeness/discover', { domain_tags: domainTags });
  return data.data;
};

// ===== Artifacts =====

export const getArtifacts = async (params?: {
  artifact_type?: string;
  domain_tags?: string[];
}): Promise<KnowledgeArtifact[]> => {
  const { data } = await knowledgeClient.get('/artifacts', { params });
  return data.data;
};

export const compileArtifacts = async (domainTags: string[]): Promise<KnowledgeArtifact[]> => {
  const { data } = await knowledgeClient.post('/artifacts/compile', { domain_tags: domainTags });
  return data.data;
};

// ===== Ingestion =====

export const ingestKnowledge = async (input: {
  text: string;
  ingestion_path?: string;
  domain_tags?: string[];
  category?: string;
}): Promise<DomainKnowledgeEntry> => {
  const { data } = await knowledgeClient.post('/knowledge', {
    ...input,
    ingestion_path: input.ingestion_path || 'conversation',
  });
  return data.data;
};
